import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { createAgendaItem } from "./invitationAgendaDefaults";
import { cyrillicToLatinSlug } from "../../utils/cyrillicToLatin";

const MAX_AGENDA_ITEMS = 5;
const AGENDA_TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

const InvitationAgendaEditor = ({
  agendaShown,
  agendaItems,
  onAgendaShownChange,
  onAgendaItemsChange,
}) => {
  const handleSwitchChange = (event) => {
    onAgendaShownChange(event.target.checked);
  };

  const ensureUniqueStep = (baseSlug, workingItems, excludeIndex) => {
    if (!baseSlug) {
      return "";
    }
    let slug = baseSlug;
    let counter = 1;
    const exists = (value) =>
      workingItems.some(
        (item, idx) => idx !== excludeIndex && item?.step === value
      );
    while (exists(slug)) {
      counter += 1;
      slug = `${baseSlug}_${counter}`;
    }
    return slug;
  };

  const updateItem = (index, field, value) => {
    const nextItems = [...(agendaItems || [])];
    const current = nextItems[index] || createAgendaItem();
    const updated = {
      ...current,
      [field]: value,
    };
    if (field === "title") {
      const possibleSlug = cyrillicToLatinSlug(value);
      if (possibleSlug) {
        updated.step = ensureUniqueStep(possibleSlug, nextItems, index);
      }
    }
    nextItems[index] = updated;
    onAgendaItemsChange(nextItems);
  };

  const handleItemChange = (index, field) => (event) => {
    updateItem(index, field, event.target.value);
  };

  const handleAddItem = () => {
    if ((agendaItems?.length || 0) >= MAX_AGENDA_ITEMS) {
      return;
    }
    const suffix = (agendaItems?.length || 0) + 1;
    const isFirstItem = !agendaItems || agendaItems.length === 0;
    const nextItem = isFirstItem
      ? createAgendaItem()
      : {
          step: `step_${suffix}`,
          title: "",
          description: "",
          time: "",
          address: "",
        };
    onAgendaItemsChange([...(agendaItems || []), nextItem]);
  };

  const handleRemoveItem = (index) => {
    const nextItems = (agendaItems || []).filter((_, idx) => idx !== index);
    onAgendaItemsChange(nextItems);
  };

  const canAddMoreAgendaItems = (agendaItems?.length || 0) < MAX_AGENDA_ITEMS;
  const isValidAgendaTime = (value) =>
    value.trim() === "" || AGENDA_TIME_REGEX.test(value.trim());

  return (
    <Box className="invitation-agenda-editor">
      <FormControlLabel
        control={<Switch checked={agendaShown} onChange={handleSwitchChange} />}
        label="Прикажи распоред / агенда"
      />

      {agendaShown && (
        <Stack spacing={2} mt={2}>
          <Typography variant="subtitle1">
            Агенда за денот
          </Typography>

          {(!agendaItems || !agendaItems.length) && (
            <Typography variant="body2" color="text.secondary">
              Нема додадено ставки. Додајте ја првата за да го дефинирате распоредот.
            </Typography>
          )}

          {(agendaItems || []).map((item, index) => (
            <Card key={`agenda-item-${index}`} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  spacing={1.5}
                  mb={2}
                >
                  <Typography variant="subtitle2">
                    Ставка #{index + 1}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveItem(index)}
                  >
                    Отстрани
                  </Button>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Време"
                      placeholder="14:00"
                      value={item?.time || ""}
                      onChange={handleItemChange(index, "time")}
                      error={!isValidAgendaTime(item?.time || "")}
                      helperText={
                        !isValidAgendaTime(item?.time || "")
                          ? "Внесете време во формат HH:MM (пример 12:30 или 19:00)."
                          : " "
                      }
                      inputProps={{ inputMode: "numeric", maxLength: 5 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Наслов"
                      value={item?.title || ""}
                      onChange={handleItemChange(index, "title")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Адреса / Локација"
                      value={item?.address || ""}
                      onChange={handleItemChange(index, "address")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Опис"
                      multiline
                      minRows={2}
                      value={item?.description || ""}
                      onChange={handleItemChange(index, "description")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Button
            variant="contained"
            onClick={handleAddItem}
            disabled={!canAddMoreAgendaItems}
            sx={{
              alignSelf: "flex-start",
              backgroundColor: "var(--primary-color)",
              "&:hover": {
                backgroundColor: "var(--primary-color-dark)",
              },
            }}
          >
            Додај ставка во агендата
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default InvitationAgendaEditor;
